import React, { useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import Text from '../../components/text/Text'
import './StoreProfileMain.css'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js'
import SvgIcon from '../../components/svgIcon/SvgIcon'
import { getCommerceInfoCommere } from '../../services/commerce'
import { useLoadingContext } from '../../context/spinnerContext'
import {
    getProfileTransaction,
    getSalesDetails,
    getSalesGraphic,
    getStoreProfileData,
    getUnbilledSales,
} from '../../services/storeProfile'
import { HomeStyles } from '../home/HomeStyles'
import { getLocal, getLocalName } from '../../Utils/Profile'

import moment from 'moment'
import { currencyFormat } from '../../formatters'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface ICommerceInfo {
    nameLocal: string
}
interface ITransaction {
    transactionId: number
    shop: {
        description: string
        address: string
    }
    transactionAmount: number
    transactionDate: string
}
const StoreProfileMain = () => {
    const loadingContext = useLoadingContext()
    const [establishmentName, setEstablishmentName] = useState('')
    const [establishmentRut, setEstablishmentRut] = useState('')
    const [contactName, setContactName] = useState('')
    const [mobilePhone, setMobilePhone] = useState('')
    const [phone, setPhone] = useState('')

    const [accountHolder, setAccountHolder] = useState('')
    const [accountDni, setAccountDni] = useState('')
    const [bank, setBank] = useState('')
    const [accountNumber, setAccountNumber] = useState('')

    const [amount, setAmount] = useState('')
    const [lastDateProforma, setLastDateProforma] = useState('')
    const [proformaEmail, setProformaEmail] = useState('')
    const [proformaIssueDay, setProformaIssueDay] = useState('')
    const [proformaPending, setProformaPending] = useState('')

    const [data, setData] = useState([])
    const [labels, setLabels] = useState([])

    const [transactions, setTransactions] = useState<ITransaction[]>([])

    const [commerceInfo, setCommerceInfo] = useState<ICommerceInfo>({
        nameLocal: '',
    })

    const currentUser = localStorage.getItem('currentUser')
    const user = currentUser && JSON.parse(currentUser)
    const commerceId = user?.sEstablecimiento + getLocal()

    const getStoreInfo = async () => {
        try {
            loadingContext?.isOpen
            const resp = await getStoreProfileData()

            if (resp) {
                setEstablishmentName(resp.establishmentName || '')
                setEstablishmentRut(resp.establishmentRut || '')

                if (
                    resp.operationalInterlocutor &&
                    resp.operationalInterlocutor[0]
                ) {
                    const interlocutor = resp.operationalInterlocutor[0]
                    setContactName(interlocutor.contactName || '')
                    setMobilePhone(interlocutor.mobilePhone || '')
                    setPhone(interlocutor.phone || '')
                }
            }
        } catch (err) {
            console.error('Error al cargar los datos de comercio:', err)
        } finally {
            loadingContext?.onClose
        }
    }

    const getStoreTransactionInfo = async () => {
        try {
            loadingContext?.isOpen
            const resp = await getProfileTransaction()

            if (resp) {
                setAccountHolder(resp.accountHolder || '')
                setAccountDni(resp.accountDni || '')
                setBank(resp.bank || '')
                setAccountNumber(resp.accountNumber || '')
            }
        } catch (err) {
            console.error('Error al cargar los datos de transferencia:', err)
        } finally {
            loadingContext?.onClose
        }
    }

    function formatMonth(month: string) {
        return month.charAt(0).toUpperCase() + month.slice(1).toLowerCase()
    }

    const getSalesGraphicInfo = async () => {
        try {
            loadingContext?.isOpen
            const resp = await getSalesGraphic()

            if (resp) {
                const data = resp.groupedTransactions.data || []
                const labels =
                    resp.groupedTransactions.labels.map((d: string) =>
                        formatMonth(d.replaceAll('.', '').split('-')[1])
                    ) || []

                setData(data)
                setLabels(labels)
            }
        } catch (err) {
            console.error('Error al cargar los datos del gráfico:', err)
        } finally {
            loadingContext?.onClose
        }
    }

    const getUnbilledSalesInfo = async () => {
        try {
            loadingContext?.isOpen
            const resp = await getUnbilledSales()

            if (resp) {
                setAmount(resp.amount || '')
                setLastDateProforma(resp.lastDateProforma || '')
                setProformaEmail(resp.billingEmail || '')
                setProformaIssueDay(resp.proformaIssueDay || 0)
                setProformaPending(resp.proformaPending || 0)
            }
        } catch (err) {
            console.error(
                'Error al cargar los datos de las ventas por facturar:',
                err
            )
        } finally {
            loadingContext?.onClose
        }
    }

    const getSalesDetailList = async () => {
        try {
            loadingContext?.isOpen
            const resp = await getSalesDetails()

            if (resp && resp.transactions) {
                setTransactions(resp.transactions)
            }
        } catch (err) {
            console.error('Error al cargar el detalle de ventas:', err)
        } finally {
            loadingContext?.onClose()
        }
    }

    React.useEffect(() => {
        getUnbilledSalesInfo()
    }, [])
    React.useEffect(() => {
        getSalesGraphicInfo()
    }, [])

    React.useEffect(() => {
        getStoreInfo()
    }, [])

    React.useEffect(() => {
        getStoreTransactionInfo()
    }, [])
    React.useEffect(() => {
        getSalesDetailList()
    }, [])
    useEffect(() => {
        getCommerceInfoCommere(commerceId).then((resp) => {
            setCommerceInfo(resp)
        })
    }, [])

    const colors = ['#184287', '#2699D6']
    const backgroundColors = labels.map((_, index) => colors[index % 2])

    const barData = {
        labels: labels,
        datasets: [
            {
                data: data,
                borderWidth: 1,
                label: '$ Monto',
                backgroundColor: backgroundColors,
                borderColor: backgroundColors,
            },
        ],
    }

    const barOptions = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function (value: any) {
                        return '$' + value.toLocaleString()
                    },
                },
            },
        },
    }

    const returnBanner = () => {
        return (
            <div style={HomeStyles.section}>
                <div style={HomeStyles.topTitleContaier}>
                    <SvgIcon iconName="IconHola" />
                    <div style={HomeStyles.topTitileTextContianer}>
                        <Text
                            color="#fff"
                            fontSize={20}
                            fontWeight={700}
                            label={`Hola ${getLocalName()}`}
                        />
                        <Text
                            color="#fff"
                            fontSize={20}
                            fontWeight={700}
                            label="¡Demosle un impulso a tu negocio!"
                        />
                    </div>
                </div>
            </div>
        )
    }
    const lastDateProformaFormated =
        moment(lastDateProforma).format('DD/MM/YYYY')
    return (
        <div className="store-container">
            <div className="store-profile-main-container">
                {returnBanner()}
                <div className="store-profile-flex">
                    <div className="store-profile-card">
                        <Text
                            label="Datos de tu comercio"
                            marginBottom={10}
                            color="#184287"
                            fontSize={19}
                            fontWeight={600}
                        />
                        <div className="store-profile-flex">
                            <Text
                                label="Razón social:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={establishmentName}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="RUT:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={establishmentRut}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="Interlocutor operativo:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={contactName ? contactName : '-'}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="Teléfono fijo:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={phone ? phone : '-'}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="Teléfono móvil:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={mobilePhone ? mobilePhone : '-'}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                    </div>
                    <div className="store-profile-card">
                        <Text
                            label="Datos de transferencia"
                            marginBottom={10}
                            color="#184287"
                            fontSize={19}
                            fontWeight={600}
                        />
                        <div className="store-profile-flex">
                            <Text
                                label="Titular de la cuenta:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={accountHolder ? accountHolder : '-'}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="RUT:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={accountDni ? accountDni : '-'}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="Banco:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={bank ? bank : '-'}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="Número de cuenta:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={accountNumber ? accountNumber : '-'}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                    </div>
                </div>
                <div className="store-profile-flex">
                    <div className="store-profile-card">
                        <Text
                            label="Ventas por facturar"
                            marginBottom={10}
                            color="#184287"
                            fontSize={19}
                            fontWeight={600}
                        />
                        <div className="store-profile-flex">
                            <Text
                                label="Monto: "
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={
                                    amount
                                        ? `${currencyFormat(Number(amount))}`
                                        : '-'
                                }
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={
                                    !isNaN(Number(amount)) && Number(amount) < 0
                                        ? '#a80000'
                                        : '#656565'
                                }
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="Día de emisión de proforma:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={proformaIssueDay}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="Mail de proforma:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={proformaEmail}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="Fecha de última proforma:"
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={lastDateProformaFormated}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={'#656565'}
                            />
                        </div>
                        <div className="store-profile-flex">
                            <Text
                                label="Proformas pendientes: "
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={700}
                                color={'#656565'}
                            />
                            <Text
                                label={`${currencyFormat(
                                    Number(proformaPending || 0)
                                )}`}
                                marginBottom={8}
                                fontSize={13}
                                fontWeight={400}
                                color={
                                    !isNaN(Number(proformaPending)) &&
                                    Number(proformaPending) < 0
                                        ? '#a80000'
                                        : '#656565'
                                }
                            />
                        </div>
                    </div>
                    <div className="store-profile-card">
                        <Text
                            label="Últimas 20 transacciones por facturar"
                            marginBottom={10}
                            color="#184287"
                            fontSize={19}
                            fontWeight={600}
                        />

                        <div
                            className="store-profile-flex store-profile-row"
                            style={{ maxHeight: 180, overflowY: 'auto' }}
                        >
                            <table className="table-20-transactions">
                                <tr>
                                    <th>
                                        <Text
                                            label="Fecha"
                                            marginBottom={8}
                                            fontSize={10}
                                            fontWeight={800}
                                            color="#656565"
                                            fontFamily={'Nunito'}
                                        />
                                    </th>
                                    <th>
                                        <Text
                                            label="Local"
                                            marginBottom={8}
                                            fontSize={10}
                                            fontWeight={800}
                                            color="#184287"
                                            fontFamily={'Nunito'}
                                        />
                                    </th>
                                    <th>
                                        <Text
                                            label="N° TX"
                                            marginBottom={8}
                                            fontSize={10}
                                            fontWeight={800}
                                            color="#656565"
                                            fontFamily={'Nunito'}
                                        />
                                    </th>
                                    <th>
                                        <Text
                                            label="Monto"
                                            marginBottom={8}
                                            fontSize={10}
                                            fontWeight={800}
                                            color="#74BE00"
                                            fontFamily={'Nunito'}
                                        />
                                    </th>
                                </tr>
                                {transactions.length > 0 ? (
                                    transactions.map((transaction) => (
                                        <tr key={transaction.transactionId}>
                                            <td>
                                                <Text
                                                    label={new Date(
                                                        transaction.transactionDate
                                                    ).toLocaleDateString()}
                                                    marginBottom={8}
                                                    fontSize={13}
                                                    fontWeight={400}
                                                    color="#656565"
                                                />
                                            </td>
                                            <td>
                                                <Text
                                                    label={
                                                        transaction.shop
                                                            .description
                                                    }
                                                    marginBottom={8}
                                                    fontSize={10}
                                                    fontWeight={600}
                                                    color="#A4A4A4"
                                                />
                                            </td>
                                            <td>
                                                <Text
                                                    label={transaction.transactionId.toString()}
                                                    marginBottom={8}
                                                    fontSize={10}
                                                    fontWeight={400}
                                                    color="#656565"
                                                />
                                            </td>
                                            <td>
                                                <Text
                                                    label={`$${transaction.transactionAmount.toLocaleString()}`} // Monto de la transacción
                                                    marginBottom={8}
                                                    fontSize={10}
                                                    fontWeight={400}
                                                    color={
                                                        !isNaN(
                                                            Number(
                                                                transaction.transactionAmount
                                                            )
                                                        ) &&
                                                        Number(
                                                            transaction.transactionAmount
                                                        ) < 0
                                                            ? '#a80000'
                                                            : '#74BE00'
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4}>
                                            No hay transacciones disponibles
                                        </td>
                                    </tr>
                                )}
                            </table>
                            {/* {transactions.length > 0 ? (
                                transactions.map((transaction) => (
                                    <div
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                        }}
                                        key={transaction.transactionId}
                                    >
                                        <div className="store-profile-column">
                                            <Text
                                                label="Fecha"
                                                marginBottom={8}
                                                fontSize={13}
                                                fontWeight={700}
                                                color="#656565"
                                            />
                                            <Text
                                                label={new Date(
                                                    transaction.transactionDate
                                                ).toLocaleDateString()}
                                                marginBottom={8}
                                                fontSize={13}
                                                fontWeight={400}
                                                color="#656565"
                                            />
                                        </div>
                                        <div className="store-profile-column">
                                            <Text
                                                label="Local N°"
                                                marginBottom={8}
                                                fontSize={13}
                                                fontWeight={700}
                                                color="#656565"
                                            />
                                            <Text
                                                label={
                                                    transaction.shop.description
                                                }
                                                marginBottom={8}
                                                fontSize={10}
                                                fontWeight={600}
                                                color="#A4A4A4"
                                            />
                                        </div>
                                        <div className="store-profile-column">
                                            <Text
                                                label="Tx"
                                                marginBottom={8}
                                                fontSize={13}
                                                fontWeight={700}
                                                color="#656565"
                                            />
                                            <Text
                                                label={transaction.transactionId.toString()}
                                                marginBottom={8}
                                                fontSize={10}
                                                fontWeight={400}
                                                color="#656565"
                                            />
                                        </div>
                                        <div className="store-profile-column">
                                            <Text
                                                label="Monto"
                                                marginBottom={8}
                                                fontSize={13}
                                                fontWeight={700}
                                                color="#656565"
                                            />
                                            <Text
                                                label={`$${transaction.transactionAmount.toLocaleString()}`} // Monto de la transacción
                                                marginBottom={8}
                                                fontSize={10}
                                                fontWeight={400}
                                                color="#74BE00"
                                            />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <Text label="No hay transacciones disponibles" />
                            )} */}
                        </div>
                    </div>
                </div>
                <div
                    className="store-profile-flex"
                    style={{ flexWrap: 'nowrap' }}
                >
                    <div className="store-profile-card">
                        <Text
                            label="Gráfico de Ventas"
                            marginBottom={10}
                            color="#184287"
                            fontSize={19}
                            fontWeight={600}
                        />
                        <Bar data={barData} options={barOptions} />
                    </div>
                    <div className="store-profile-card-hidden"></div>
                </div>
            </div>
        </div>
    )
}

export default StoreProfileMain
